import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { adminApi, CreateClientAccountRequest } from '../api/admin';
import { providersApi, Provider, Currency, currenciesApi } from '../api/providers';
import { Eye, X, Plus, Search, ChevronDown, ChevronUp, ChevronsUpDown, UserCheck } from 'lucide-react';
import Modal from '../components/common/Modal';
import { useSelectedClientStore } from '../store/selectedClientStore';
import AccountRequestsPanel from '../components/AccountRequestsPanel';
import AccountHistoryPanel from '../components/AccountHistoryPanel';
import { formatPublicIdDisplay } from '../utils/formatPublicId';
import { getClientDisplayName, getClientOptionLabel } from '../utils/clientDisplay';
import { validateBankingIdentifiers } from '../utils/bankingValidation';
import AccountRequisitesPanel from '../components/AccountRequisitesPanel';

const ACCOUNT_STATUS_COLORS: Record<string, string> = {
  ACTIVE:     'bg-green-100 text-green-800',
  PENDING:    'bg-yellow-100 text-yellow-800',
  FROZEN:     'bg-blue-100 text-blue-800',
  RESTRICTED: 'bg-orange-100 text-orange-800',
  CLOSED:     'bg-red-100 text-red-800',
};

function StatusBadge({ status }: { status: string }) {
  const cls = ACCOUNT_STATUS_COLORS[status?.toUpperCase()] ?? 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

// Allowed status transitions per business spec
const ALLOWED_TRANSITIONS: Record<string, string[]> = {
  PENDING:    ['ACTIVE', 'CLOSED'],
  ACTIVE:     ['FROZEN', 'RESTRICTED', 'CLOSED'],
  FROZEN:     ['ACTIVE', 'RESTRICTED', 'CLOSED'],
  RESTRICTED: ['ACTIVE', 'FROZEN', 'CLOSED'],
  CLOSED:     [],
};

function statusChangeRequiresComment(currentStatus: string, newStatus: string): boolean {
  const current = currentStatus.trim().toUpperCase();
  const target = newStatus.trim().toUpperCase();
  return target === 'FROZEN' || (current === 'FROZEN' && target === 'ACTIVE');
}

function statusChangeActionLabel(newStatus: string): string {
  switch (newStatus.trim().toUpperCase()) {
    case 'FROZEN': return 'Freeze account';
    case 'ACTIVE': return 'Unfreeze account';
    case 'RESTRICTED': return 'Restrict account';
    case 'CLOSED': return 'Close account';
    default: return `Change status to ${newStatus}`;
  }
}

interface Account {
  id: string;
  clientId: string;
  clientPublicId?: string;
  currencyId: string;
  status: string;
  clientFullName: string;
  providerName: string | null;
  accountName: string | null;
}

type SortField = 'clientFullName' | 'currencyId' | 'providerName' | 'status' | 'accountName';
type SortDir = 'asc' | 'desc';

interface SubAccount {
  id?: string;
  displayName?: string;
  currency?: string;
  currencyCode?: string;
  status?: string;
  balance?: number;
}

interface AccountDetails {
  id: string;
  clientId: string;
  providerId?: string;
  currencyId: string;
  status: string;
  clientFullName: string;
  clientPublicId?: string;
  providerName?: string;
  // Banking details
  accountName?: string;
  accountNumber?: string;
  iban?: string;
  bankName?: string;
  branchCode?: string;
  bankCode?: string;
  bankLocation?: string;
  bankAddress?: string;
  swiftCode?: string;
  intermediaryBankName?: string;
  intermediaryBankSwift?: string;
  totalBalance?: number;
  availableAccount: SubAccount;
  blockedAccount: SubAccount;
  incomingPendingAccount: SubAccount;
  outgoingPendingAccount: SubAccount;
  restrictedAccount: SubAccount;
}

interface ClientOption {
  id: string;
  label: string;
  publicId?: string;
  email?: string;
}

const EMPTY_FORM: CreateClientAccountRequest = {
  clientId: '',
  providerId: '',
  currencyId: '',
  accountName: '',
  accountNumber: '',
  iban: '',
  bankName: '',
  branchCode: '',
  bankCode: '',
  bankLocation: '',
  bankAddress: '',
  swiftCode: '',
  intermediaryBankName: '',
  intermediaryBankSwift: '',
};

type PageTab = 'accounts' | 'requests' | 'history';

function AccountsPage() {
  const { selectedClient, clearSelectedClient } = useSelectedClientStore();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState<PageTab>(
    (location.state as { tab?: PageTab } | null)?.tab ?? 'accounts'
  );
  const [pendingCount, setPendingCount] = useState<number | null>(null);

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAccountDetails, setSelectedAccountDetails] = useState<AccountDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Status change
  const [statusChanging, setStatusChanging] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{ accountId: string; newStatus: string } | null>(null);
  const [statusChangeComment, setStatusChangeComment] = useState('');
  const [statusChangeError, setStatusChangeError] = useState('');

  // Suspense release
  const [suspenseReleasing, setSuspenseReleasing] = useState(false);
  const [suspenseMsg, setSuspenseMsg] = useState('');

  // Filter & sort state (shared)
  const [search, setSearch] = useState('');
  const [filterCurrency, setFilterCurrency] = useState('');
  const [filterProvider, setFilterProvider] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortField, setSortField] = useState<SortField>('clientFullName');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedClientLabel, setSelectedClientLabel] = useState('');
  const [providers, setProviders] = useState<Provider[]>([]);
  const [filteredCurrencies, setFilteredCurrencies] = useState<Currency[]>([]);
  const [allCurrencies, setAllCurrencies] = useState<Currency[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set());

  const [form, setForm] = useState<CreateClientAccountRequest>(EMPTY_FORM);

  useEffect(() => {
    fetchAccounts();
    void preloadCurrencies();
  }, []);

  const preloadCurrencies = async () => {
    try {
      const data = await currenciesApi.getAll();
      setAllCurrencies(data || []);
    } catch {
      // Keep page usable even if currency dictionary request fails.
    }
  };

  const currencyCodeById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of allCurrencies) {
      if (!c?.id || !c?.code) continue;
      map.set(c.id.toUpperCase(), c.code.toUpperCase());
    }
    return map;
  }, [allCurrencies]);

  const currencyLabel = (currencyId: string | null | undefined) => {
    const raw = (currencyId || '').trim();
    if (!raw) return '—';
    return currencyCodeById.get(raw.toUpperCase()) || raw.toUpperCase();
  };


  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const [accountsResp, requestsResp] = await Promise.all([
        adminApi.getClientAccounts(),
        adminApi.getAccountOpeningRequests().catch(() => ({ data: [] as { status?: string }[] })),
      ]);
      const data: any = accountsResp.data;
      setAccounts(Array.isArray(data) ? data : (data?.data || []));
      const reqRaw = requestsResp.data as unknown;
      const reqList = Array.isArray(reqRaw)
        ? reqRaw
        : ((reqRaw as { data?: { status?: string }[] })?.data ?? []);
      const pending = reqList.filter(r => r.status?.toUpperCase() === 'PENDING').length;
      setPendingCount(pending);
    } catch {
      setError('Failed to load accounts');
      setPendingCount(0);
    } finally {
      setLoading(false);
    }
  };

  const filteredAccounts = useMemo(() => {
    const q = search.trim().toLowerCase();
    let result = accounts.filter(acc => {
      if (selectedClient && acc.clientId !== selectedClient.id) return false;
      if (q) {
        const matchName = acc.clientFullName?.toLowerCase().includes(q);
        const matchId   = acc.id.toLowerCase().includes(q) || acc.clientId?.toLowerCase().includes(q) || acc.clientPublicId?.toLowerCase().includes(q);
        const matchAccName = acc.accountName?.toLowerCase().includes(q);
        if (!matchName && !matchId && !matchAccName) return false;
      }
      if (filterCurrency && acc.currencyId !== filterCurrency) return false;
      if (filterProvider && (acc.providerName || '') !== filterProvider) return false;
      if (filterStatus && acc.status !== filterStatus) return false;
      return true;
    });

    result = [...result].sort((a, b) => {
      const av = (a[sortField] ?? '').toString().toLowerCase();
      const bv = (b[sortField] ?? '').toString().toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });

    return result;
  }, [accounts, search, filterCurrency, filterProvider, filterStatus, sortField, sortDir, selectedClient]);

  const uniqueCurrencyOptions = useMemo(
    () =>
      Array.from(new Set(accounts.map(a => a.currencyId)))
        .sort()
        .map((id) => ({ id, label: currencyCodeById.get(id.toUpperCase()) || id.toUpperCase() })),
    [accounts, currencyCodeById]
  );
  const uniqueProviders  = useMemo(() => Array.from(new Set(accounts.map(a => a.providerName).filter((p): p is string => !!p))).sort(), [accounts]);


  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const fetchModalData = async () => {
    try {
      const [providersResp, currenciesResp] = await Promise.all([
        providersApi.getAll(),
        currenciesApi.getAll(),
      ]);
      setProviders(providersResp);
      setAllCurrencies(currenciesResp);
      setFilteredCurrencies([]);
    } catch {
      setFormError('Failed to load required data');
    }
  };

  const openModal = () => {
    setForm({
      ...EMPTY_FORM,
      clientId: selectedClient?.id ?? '',
    });
    setSelectedClientLabel(selectedClient?.fullName ?? '');
    setFieldErrors(new Set());
    setFormError('');
    setShowCreateModal(true);
    fetchModalData();
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setForm(EMPTY_FORM);
    setSelectedClientLabel('');
    setFieldErrors(new Set());
    setFormError('');
    setFilteredCurrencies([]);
  };

  const handleProviderChange = (providerId: string) => {
    setForm(prev => ({ ...prev, providerId, currencyId: '' }));
    clearFieldError('Provider');

    if (providerId) {
      const provider = providers.find(p => p.id === providerId);
      if (provider) {
        const providerCurrencyIds = new Set(provider.currencies.map(c => c.currencyId));
        setFilteredCurrencies(allCurrencies.filter(c => providerCurrencyIds.has(c.id)));
      } else {
        setFilteredCurrencies([]);
      }
    } else {
      setFilteredCurrencies([]);
    }
  };

  const clearFieldError = (field: string) => {
    setFieldErrors(prev => {
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  };

  const handleChange = (field: keyof CreateClientAccountRequest, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    const labelMap: Partial<Record<keyof CreateClientAccountRequest, string>> = {
      clientId: 'Client',
      providerId: 'Provider',
      currencyId: 'Currency',
      accountName: 'Account Name',
      accountNumber: 'Account Number',
      iban: 'IBAN',
      bankName: 'Bank Name',
      bankLocation: 'Bank Location',
      bankAddress: 'Bank Address',
      swiftCode: 'SWIFT Code',
    };
    const label = labelMap[field];
    if (label) clearFieldError(label);
    if (field === 'accountNumber' || field === 'iban') {
      clearFieldError('Account Number');
      clearFieldError('IBAN');
    }
  };

  const validate = (): boolean => {
    const required: { field: keyof CreateClientAccountRequest; label: string }[] = [
      { field: 'clientId',     label: 'Client' },
      { field: 'providerId',   label: 'Provider' },
      { field: 'currencyId',   label: 'Currency' },
      { field: 'accountName',  label: 'Account Name' },
      { field: 'bankName',     label: 'Bank Name' },
      { field: 'bankLocation', label: 'Bank Location' },
      { field: 'bankAddress',  label: 'Bank Address' },
      { field: 'swiftCode',    label: 'SWIFT Code' },
    ];

    const empty = required.filter(({ field }) => !form[field]?.toString().trim());
    const identifierError = validateBankingIdentifiers(form.accountNumber, form.iban);
    const errors = new Set(empty.map(e => e.label));

    if (identifierError) {
      errors.add('Account Number');
      errors.add('IBAN');
    }

    if (errors.size > 0) {
      setFieldErrors(errors);
      if (identifierError && empty.length === 0) {
        setFormError(identifierError);
      } else if (identifierError) {
        setFormError(`${identifierError}. Also fill in: ${empty.map(e => e.label).join(', ')}`);
      } else {
        setFormError(`Please fill in required fields: ${empty.map(e => e.label).join(', ')}`);
      }
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setFieldErrors(new Set());

    if (!validate()) return;

    try {
      setSubmitting(true);
      await adminApi.createClientAccount({
        ...form,
        accountNumber: form.accountNumber?.trim() || undefined,
        iban: form.iban?.trim() || undefined,
        branchCode: form.branchCode?.trim() || undefined,
        bankCode: form.bankCode?.trim() || undefined,
        intermediaryBankName: form.intermediaryBankName?.trim() || undefined,
        intermediaryBankSwift: form.intermediaryBankSwift?.trim() || undefined,
      });
      closeModal();
      await fetchAccounts();
    } catch (err: any) {
      setFormError(err.response?.data?.message || err.response?.data || err.message || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  const fieldClass = (label: string) => {
    const base = 'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm';
    return fieldErrors.has(label)
      ? `${base} border-red-400 bg-red-50`
      : `${base} border-gray-300`;
  };

  const handleViewDetails = async (accountId: string) => {
    try {
      setLoadingDetails(true);
      const response = await adminApi.getClientAccountById(accountId);
      setSelectedAccountDetails(response.data);
    } catch {
      setError('Failed to load account details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleStatusChange = async (accountId: string, newStatus: string, comment?: string) => {
    try {
      setStatusChanging(true);
      setStatusChangeError('');
      setSuspenseMsg('');
      await adminApi.updateAccountStatus(accountId, newStatus, comment);
      const response = await adminApi.getClientAccountById(accountId);
      setSelectedAccountDetails(response.data);
      await fetchAccounts();
      setPendingStatusChange(null);
      setStatusChangeComment('');
    } catch (err: any) {
      const d = err.response?.data;
      const msg = typeof d === 'string' ? d : (d?.error || d?.message || err.message || 'Failed to update status');
      setStatusChangeError(msg);
    } finally {
      setStatusChanging(false);
    }
  };

  const requestStatusChange = (accountId: string, newStatus: string) => {
    const currentStatus = selectedAccountDetails?.status || '';
    setStatusChangeError('');
    if (statusChangeRequiresComment(currentStatus, newStatus)) {
      setPendingStatusChange({ accountId, newStatus });
      setStatusChangeComment('');
      return;
    }
    void handleStatusChange(accountId, newStatus);
  };

  const confirmStatusChange = () => {
    if (!pendingStatusChange) return;
    const comment = statusChangeComment.trim();
    if (!comment) {
      setStatusChangeError('Comment/reason is required for this status change.');
      return;
    }
    void handleStatusChange(pendingStatusChange.accountId, pendingStatusChange.newStatus, comment);
  };

  const cancelStatusChange = () => {
    setPendingStatusChange(null);
    setStatusChangeComment('');
    setStatusChangeError('');
  };

  const handleReleaseSuspense = async (accountId: string) => {
    try {
      setSuspenseReleasing(true);
      setSuspenseMsg('');
      const resp = await adminApi.releaseSuspenseFunds(accountId);
      const { released } = resp.data;
      setSuspenseMsg(
        released > 0
          ? `Released ${released.toFixed(2)} from suspense to IncomingPending.`
          : 'No suspense funds to release.'
      );
      // Refresh details to show updated balances
      const details = await adminApi.getClientAccountById(accountId);
      setSelectedAccountDetails(details.data);
    } catch (err: any) {
      const d = err.response?.data;
      setSuspenseMsg(typeof d === 'string' ? d : (d?.error || 'Failed to release suspense funds'));
    } finally {
      setSuspenseReleasing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <PageHeader activeTab={activeTab} onTabChange={setActiveTab} pendingCount={pendingCount} />
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  if (error && !showCreateModal) {
    return (
      <div className="p-6">
        <PageHeader activeTab={activeTab} onTabChange={setActiveTab} pendingCount={pendingCount} />
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <PageHeader activeTab={activeTab} onTabChange={setActiveTab} pendingCount={pendingCount} />

      {activeTab === 'requests' ? (
        <AccountRequestsPanel onPendingCountChange={setPendingCount} />
      ) : activeTab === 'history' ? (
        <AccountHistoryPanel />
      ) : (<>

      {selectedClient && (
        <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-lg">
          <UserCheck className="h-4 w-4 text-indigo-600 flex-shrink-0" />
          <p className="text-sm text-indigo-800 flex-1">
            Showing accounts for: <span className="font-semibold">{getClientDisplayName(selectedClient) || selectedClient.fullName}</span>
            {selectedClient.publicId && <span className="ml-2 font-mono text-indigo-600">{formatPublicIdDisplay(selectedClient.publicId)}</span>}
          </p>
          <button
            onClick={clearSelectedClient}
            className="text-indigo-400 hover:text-indigo-600 transition-colors"
            title="Show all accounts"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              {selectedClient ? `${getClientDisplayName(selectedClient) || selectedClient.fullName}'s Accounts` : 'All Accounts'}
            </h3>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">
                {filteredAccounts.length} / {accounts.length} accounts
              </span>
              <button
                onClick={openModal}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Account
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-3 border-b border-gray-100 bg-gray-50 flex flex-wrap gap-3 items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by client name, public ID (XC-…)…"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Currency */}
          <select
            value={filterCurrency}
            onChange={e => setFilterCurrency(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">All currencies</option>
            {uniqueCurrencyOptions.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>

          {/* Provider */}
          <select
            value={filterProvider}
            onChange={e => setFilterProvider(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">All providers</option>
            {uniqueProviders.map(p => <option key={p} value={p}>{p}</option>)}
          </select>

          {/* Status */}
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">All statuses</option>
            <option value="ACTIVE">ACTIVE</option>
            <option value="PENDING">PENDING</option>
            <option value="FROZEN">FROZEN</option>
            <option value="RESTRICTED">RESTRICTED</option>
            <option value="CLOSED">CLOSED</option>
          </select>

          {/* Reset */}
          {(search || filterCurrency || filterProvider || filterStatus) && (
            <button
              onClick={() => { setSearch(''); setFilterCurrency(''); setFilterProvider(''); setFilterStatus(''); }}
              className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Account / Ledger ID
                </th>
                <SortTh label="Client Name"    field="clientFullName" current={sortField} dir={sortDir} onSort={handleSort} />
                <SortTh label="Account Name"   field="accountName"    current={sortField} dir={sortDir} onSort={handleSort} />
                <SortTh label="Currency"       field="currencyId"     current={sortField} dir={sortDir} onSort={handleSort} />
                <SortTh label="Provider"       field="providerName"   current={sortField} dir={sortDir} onSort={handleSort} />
                <SortTh label="Status"         field="status"         current={sortField} dir={sortDir} onSort={handleSort} />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAccounts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-400">
                    No accounts match the current filters
                  </td>
                </tr>
              ) : filteredAccounts.map(account => (
                <tr key={account.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-xs text-gray-500 font-mono">{account.id}</div>
                    {account.clientPublicId && (
                      <div className="text-xs text-blue-500 font-mono mt-0.5">{formatPublicIdDisplay(account.clientPublicId)}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {account.clientFullName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {account.accountName || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {currencyLabel(account.currencyId)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {account.providerName || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge status={account.status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleViewDetails(account.id)}
                      className="inline-flex items-center px-3 py-1.5 text-blue-600 hover:text-blue-900"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Account Details Overlay */}
      {selectedAccountDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-40 overflow-y-auto z-50">
          <div className="min-h-screen flex items-start justify-center py-10 px-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl">

              {/* Header */}
              <div className="flex items-start justify-between p-6 border-b border-gray-200">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Account Details</h3>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-sm text-gray-600">{selectedAccountDetails.clientFullName}</span>
                    {selectedAccountDetails.clientPublicId && (
                      <span className="text-xs font-mono text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded">
                        {formatPublicIdDisplay(selectedAccountDetails.clientPublicId)}
                      </span>
                    )}
                    {selectedAccountDetails.providerName && (
                      <span className="text-xs text-gray-400">· {selectedAccountDetails.providerName}</span>
                    )}
                  </div>
                </div>
                <button onClick={() => { setSelectedAccountDetails(null); setSuspenseMsg(''); cancelStatusChange(); }} className="text-gray-400 hover:text-gray-600 mt-1">
                  <X className="h-6 w-6" />
                </button>
              </div>

              {loadingDetails ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              ) : (
                <div className="p-6 space-y-6">

                  {/* ── Summary bar ── */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-blue-50 border border-blue-100 rounded-xl p-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Account ID</p>
                      <p className="text-xs font-mono text-gray-700 break-all">{selectedAccountDetails.id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Currency</p>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                        {currencyLabel(selectedAccountDetails.currencyId)}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</p>
                      <StatusBadge status={selectedAccountDetails.status} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Total Balance</p>
                      <p className="text-xl font-bold text-blue-700 mt-1">
                        {(selectedAccountDetails.totalBalance ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        <span className="text-sm font-normal text-blue-400 ml-1">{currencyLabel(selectedAccountDetails.currencyId)}</span>
                      </p>
                    </div>
                  </div>

                  {/* ── Status transitions ── */}
                  {(() => {
                    const transitions = ALLOWED_TRANSITIONS[selectedAccountDetails.status?.toUpperCase()] ?? [];
                    if (transitions.length === 0) return null;
                    const colors: Record<string, string> = {
                      ACTIVE:     'bg-green-600 hover:bg-green-700 text-white',
                      FROZEN:     'bg-blue-600 hover:bg-blue-700 text-white',
                      RESTRICTED: 'bg-orange-500 hover:bg-orange-600 text-white',
                      CLOSED:     'bg-red-600 hover:bg-red-700 text-white',
                    };
                    return (
                      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg flex-wrap">
                        <span className="text-xs font-medium text-gray-500">Change status:</span>
                        {transitions.map(s => (
                          <button
                            key={s}
                            onClick={() => requestStatusChange(selectedAccountDetails.id, s)}
                            disabled={statusChanging}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50 ${colors[s] || 'bg-gray-500 text-white hover:bg-gray-600'}`}
                          >
                            {statusChanging ? '…' : `→ ${s}`}
                          </button>
                        ))}
                        {statusChangeError && !pendingStatusChange && (
                          <p className="w-full text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                            {statusChangeError}
                          </p>
                        )}
                      </div>
                    );
                  })()}

                  {/* ── Release Suspense Funds (visible when ACTIVE, in case account was previously frozen) ── */}
                  {selectedAccountDetails.status?.toUpperCase() === 'ACTIVE' && (
                    <div className="flex items-start gap-3 px-4 py-3 bg-teal-50 border border-teal-200 rounded-lg">
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-teal-800">Suspense Funds</p>
                        <p className="text-xs text-teal-600 mt-0.5">
                          If this account was previously frozen, incoming payments may be parked in the provider's Suspense account.
                          Use this action to transfer them to the client's IncomingPending account.
                        </p>
                        {suspenseMsg && (
                          <p className={`text-xs mt-1 font-medium ${suspenseMsg.includes('Released') ? 'text-teal-700' : 'text-gray-600'}`}>
                            {suspenseMsg}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleReleaseSuspense(selectedAccountDetails.id)}
                        disabled={suspenseReleasing}
                        className="flex-shrink-0 px-3 py-1.5 text-xs font-semibold bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
                      >
                        {suspenseReleasing ? 'Releasing…' : 'Release Suspense'}
                      </button>
                    </div>
                  )}

                  <AccountRequisitesPanel
                    accountId={selectedAccountDetails.id}
                    allowSepa={
                      (selectedAccountDetails.providerName || '').trim().toUpperCase() === 'CURRENCY CLOUD'
                      && currencyLabel(selectedAccountDetails.currencyId).toUpperCase() === 'EUR'
                    }
                  />

                  {hasLegacyBankingDetails(selectedAccountDetails) && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Legacy banking details</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 bg-gray-50 border border-gray-200 rounded-xl p-4">
                        <DetailRow label="Account Name"   value={selectedAccountDetails.accountName} />
                        <DetailRow label="Account Number" value={selectedAccountDetails.accountNumber} mono />
                        <DetailRow label="IBAN"           value={selectedAccountDetails.iban} mono />
                        <DetailRow label="Bank Name"      value={selectedAccountDetails.bankName} />
                        <DetailRow label="SWIFT Code"     value={selectedAccountDetails.swiftCode} mono />
                        <DetailRow label="Bank Location"  value={selectedAccountDetails.bankLocation} />
                        <DetailRow label="Branch Code"    value={selectedAccountDetails.branchCode} mono />
                        <DetailRow label="Bank Code"      value={selectedAccountDetails.bankCode} mono />
                        <DetailRow label="Intermediary Bank Name"  value={selectedAccountDetails.intermediaryBankName} />
                        <DetailRow label="Intermediary Bank SWIFT" value={selectedAccountDetails.intermediaryBankSwift} mono />
                        <div className="sm:col-span-2">
                          <DetailRow label="Bank Address" value={selectedAccountDetails.bankAddress} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Sub-Accounts ── */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Sub-Accounts</h4>
                    <div className="grid grid-cols-1 gap-3">
                      {([
                        { label: 'Available',         data: selectedAccountDetails.availableAccount,        color: 'green'  },
                        { label: 'Blocked',           data: selectedAccountDetails.blockedAccount,          color: 'red'    },
                        { label: 'Incoming Pending',  data: selectedAccountDetails.incomingPendingAccount,  color: 'blue'   },
                        { label: 'Outgoing Pending',  data: selectedAccountDetails.outgoingPendingAccount,  color: 'orange' },
                        { label: 'Restricted',        data: selectedAccountDetails.restrictedAccount,       color: 'gray'   },
                      ] as const).map(({ label, data, color }) => (
                        <div key={label} className="border border-gray-200 rounded-lg px-4 py-3 flex items-center gap-4 hover:bg-gray-50">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-sm font-medium text-gray-900">{label}</span>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                data?.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {data?.status ?? '—'}
                              </span>
                            </div>
                            {data?.id && (
                              <p className="text-xs text-gray-400 font-mono truncate">{data.id}</p>
                            )}
                            {data?.displayName && (
                              <p className="text-xs text-gray-400">{data.displayName}</p>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className={`text-base font-bold ${
                              color === 'green'  ? 'text-green-600'  :
                              color === 'red'    ? 'text-red-600'    :
                              color === 'blue'   ? 'text-blue-600'   :
                              color === 'orange' ? 'text-orange-600' :
                              'text-gray-600'
                            }`}>
                              {((data as any)?.balance ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                            <p className="text-xs text-gray-400">{currencyLabel(data?.currencyCode || selectedAccountDetails.currencyId)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end pt-2">
                    <button
                      onClick={() => { setSelectedAccountDetails(null); setSuspenseMsg(''); cancelStatusChange(); }}
                      className="px-5 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Account Modal */}
      <Modal isOpen={showCreateModal} onClose={closeModal} title="Create New Account" size="lg">
        <form onSubmit={handleSubmit} noValidate>
          {formError && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {formError}
            </div>
          )}

          {/* ── Selectors ── */}
          <div className="grid grid-cols-1 gap-4 mb-4">
            {/* Client — searchable combobox (backend search) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client <span className="text-red-500">*</span>
              </label>
              <ClientCombobox
                value={form.clientId}
                selectedLabel={selectedClientLabel}
                onChange={(id, label) => {
                  handleChange('clientId', id);
                  setSelectedClientLabel(label);
                }}
                hasError={fieldErrors.has('Client')}
              />
            </div>

            {/* Provider */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Provider <span className="text-red-500">*</span>
              </label>
              <select
                value={form.providerId}
                onChange={e => handleProviderChange(e.target.value)}
                className={fieldClass('Provider')}
              >
                <option value="">Select provider</option>
                {providers.map(p => (
                  <option key={p.id} value={p.id!}>{p.name}</option>
                ))}
              </select>
            </div>

            {/* Currency */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Currency <span className="text-red-500">*</span>
              </label>
              <select
                value={form.currencyId}
                onChange={e => handleChange('currencyId', e.target.value)}
                className={fieldClass('Currency')}
                disabled={!form.providerId}
              >
                <option value="">
                  {form.providerId ? 'Select currency' : 'Select provider first'}
                </option>
                {filteredCurrencies.map(c => (
                  <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                ))}
              </select>
            </div>
          </div>

          <hr className="my-4 border-gray-200" />
          <p className="text-xs text-gray-500 mb-3">Bank account details</p>

          {/* ── Banking fields ── */}
          <div className="grid grid-cols-1 gap-4">
            {/* Account Name */}
            <FormField
              label="Account Name"
              required
              error={fieldErrors.has('Account Name')}
            >
              <input
                type="text"
                value={form.accountName}
                onChange={e => handleChange('accountName', e.target.value)}
                className={fieldClass('Account Name')}
                placeholder="Enter account name"
              />
            </FormField>

            {/* Account Number */}
            <FormField label="Account Number" error={fieldErrors.has('Account Number')}>
              <input
                type="text"
                value={form.accountNumber || ''}
                onChange={e => handleChange('accountNumber', e.target.value)}
                className={fieldClass('Account Number')}
                placeholder="Enter account number"
              />
            </FormField>

            {/* IBAN */}
            <FormField label="IBAN" error={fieldErrors.has('IBAN')}>
              <input
                type="text"
                value={form.iban || ''}
                onChange={e => handleChange('iban', e.target.value.toUpperCase())}
                className={fieldClass('IBAN')}
                placeholder="e.g. BH13SGBD79621400000128"
              />
            </FormField>
            <p className="text-xs text-gray-500 -mt-2">Fill in Account Number, IBAN, or both.</p>

            {/* Bank Name */}
            <FormField label="Bank Name" required error={fieldErrors.has('Bank Name')}>
              <input
                type="text"
                value={form.bankName}
                onChange={e => handleChange('bankName', e.target.value)}
                className={fieldClass('Bank Name')}
                placeholder="Enter bank name"
              />
            </FormField>

            {/* Branch Code + Bank Code (optional, side by side) */}
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Branch Code">
                <input
                  type="text"
                  value={form.branchCode || ''}
                  onChange={e => handleChange('branchCode', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Optional"
                />
              </FormField>
              <FormField label="Bank Code">
                <input
                  type="text"
                  value={form.bankCode || ''}
                  onChange={e => handleChange('bankCode', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Optional"
                />
              </FormField>
            </div>

            {/* Bank Location */}
            <FormField label="Bank Location" required error={fieldErrors.has('Bank Location')}>
              <input
                type="text"
                value={form.bankLocation}
                onChange={e => handleChange('bankLocation', e.target.value)}
                className={fieldClass('Bank Location')}
                placeholder="City, Country"
              />
            </FormField>

            {/* Bank Address */}
            <FormField label="Bank Address" required error={fieldErrors.has('Bank Address')}>
              <textarea
                value={form.bankAddress}
                onChange={e => handleChange('bankAddress', e.target.value)}
                className={fieldClass('Bank Address')}
                placeholder="Full bank address"
                rows={2}
              />
            </FormField>

            {/* SWIFT Code */}
            <FormField label="SWIFT Code" required error={fieldErrors.has('SWIFT Code')}>
              <input
                type="text"
                value={form.swiftCode}
                onChange={e => handleChange('swiftCode', e.target.value.toUpperCase())}
                className={fieldClass('SWIFT Code')}
                placeholder="e.g. DEUTDEDB"
                maxLength={11}
              />
            </FormField>

            <div className="grid grid-cols-1 gap-4">
              <FormField label="Intermediary Bank Name">
                <input
                  type="text"
                  value={form.intermediaryBankName || ''}
                  onChange={e => handleChange('intermediaryBankName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Optional"
                />
              </FormField>
              <FormField label="Intermediary Bank SWIFT">
                <input
                  type="text"
                  value={form.intermediaryBankSwift || ''}
                  onChange={e => handleChange('intermediaryBankSwift', e.target.value.toUpperCase())}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Optional"
                  maxLength={11}
                />
              </FormField>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              {submitting ? 'Creating…' : 'Create Account'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={!!pendingStatusChange}
        onClose={cancelStatusChange}
        title={pendingStatusChange ? statusChangeActionLabel(pendingStatusChange.newStatus) : 'Change account status'}
        size="md"
      >
        <p className="text-sm text-gray-600 mb-4">
          {pendingStatusChange?.newStatus.toUpperCase() === 'FROZEN'
            ? 'A comment is required when freezing an account. Active outgoing payments will be placed on hold.'
            : 'A comment is required when unfreezing an account.'}
        </p>
        {statusChangeError && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {statusChangeError}
          </div>
        )}
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Reason <span className="text-red-500">*</span>
        </label>
        <textarea
          value={statusChangeComment}
          onChange={(e) => {
            setStatusChangeComment(e.target.value);
            if (statusChangeError) setStatusChangeError('');
          }}
          rows={4}
          placeholder="e.g. Client requested temporary hold — account not in use yet"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={cancelStatusChange}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirmStatusChange}
            disabled={statusChanging || !statusChangeComment.trim()}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            {statusChanging ? 'Saving…' : 'Confirm'}
          </button>
        </div>
      </Modal>
      </>)}
    </div>
  );
}

/* ── Small helpers ── */

function SortTh({
  label, field, current, dir, onSort,
}: {
  label: string;
  field: SortField;
  current: SortField;
  dir: SortDir;
  onSort: (f: SortField) => void;
}) {
  const active = current === field;
  return (
    <th
      onClick={() => onSort(field)}
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100"
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active
          ? dir === 'asc'
            ? <ChevronUp className="w-3 h-3 text-blue-500" />
            : <ChevronDown className="w-3 h-3 text-blue-500" />
          : <ChevronsUpDown className="w-3 h-3 text-gray-300" />}
      </span>
    </th>
  );
}

function PageHeader({
  activeTab,
  onTabChange,
  pendingCount,
}: {
  activeTab: PageTab;
  onTabChange: (t: PageTab) => void;
  pendingCount: number | null;
}) {
  const tabs: { id: PageTab; label: string; badge?: React.ReactNode }[] = [
    { id: 'accounts',  label: 'Client Accounts' },
    {
      id: 'requests',
      label: 'Account Requests',
      badge: !!pendingCount && (
        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800">
          {pendingCount}
        </span>
      ),
    },
    { id: 'history', label: 'History' },
  ];

  return (
    <div className="mb-6">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold text-gray-900">Accounts</h1>
        <p className="text-gray-600">Manage client accounts and balances</p>
      </div>
      <div className="flex border-b border-gray-200">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px flex items-center gap-2 ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.label}
            {tab.badge}
          </button>
        ))}
      </div>
    </div>
  );
}

function hasLegacyBankingDetails(account: AccountDetails): boolean {
  return Boolean(
    account.accountName?.trim() ||
    account.accountNumber?.trim() ||
    account.iban?.trim() ||
    account.bankName?.trim() ||
    account.swiftCode?.trim() ||
    account.bankLocation?.trim() ||
    account.branchCode?.trim() ||
    account.bankCode?.trim() ||
    account.intermediaryBankName?.trim() ||
    account.intermediaryBankSwift?.trim() ||
    account.bankAddress?.trim()
  );
}

function DetailRow({
  label,
  value,
  mono,
  showEmpty,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
  showEmpty?: boolean;
}) {
  const text = value?.trim();
  if (!text && !showEmpty) return null;
  return (
    <div>
      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
      <p className={`text-sm text-gray-800 ${mono ? 'font-mono' : ''}`}>{text || '—'}</p>
    </div>
  );
}

function FormField({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className={`block text-sm font-medium mb-1 ${error ? 'text-red-600' : 'text-gray-700'}`}>
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

/* ── Searchable Client Combobox (backend search + debounce) ─────────────── */

interface ClientComboboxProps {
  value: string;
  selectedLabel: string;
  onChange: (id: string, label: string) => void;
  hasError?: boolean;
}

function ClientCombobox({ value, selectedLabel, onChange, hasError }: ClientComboboxProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<ClientOption[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
        setResults([]);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced backend search
  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setQuery(q);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!q.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const resp = await adminApi.searchClients(q.trim());
        const raw: any[] = Array.isArray(resp.data)
          ? resp.data
          : (resp.data as any)?.data ?? [];
        setResults(raw.map((c: any) => ({
          id: c.id,
          label: getClientOptionLabel(c) || formatPublicIdDisplay(c.publicId) || c.id,
          publicId: c.publicId,
          email: c.email,
        })));
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleOpen = () => {
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleSelect = (opt: ClientOption) => {
    onChange(opt.id, opt.label);
    setOpen(false);
    setQuery('');
    setResults([]);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('', '');
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const borderColor = hasError ? 'border-red-400 bg-red-50' : 'border-gray-300';

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger / Input */}
      <div
        onClick={!open ? handleOpen : undefined}
        className={`flex items-center w-full px-3 py-2 border rounded-lg bg-white text-sm ${borderColor} ${open ? 'ring-2 ring-blue-500 border-blue-500' : 'cursor-pointer'}`}
      >
        {open ? (
          <input
            ref={inputRef}
            value={query}
            onChange={handleQueryChange}
            onClick={e => e.stopPropagation()}
            placeholder="Search by name, email or ID (XC-…)…"
            className="flex-1 outline-none bg-transparent text-gray-800 placeholder-gray-400"
          />
        ) : (
          <span className={`flex-1 truncate ${value ? 'text-gray-900' : 'text-gray-400'}`}>
            {value ? selectedLabel : 'Select client'}
          </span>
        )}

        <div className="flex items-center gap-1 ml-2 text-gray-400 flex-shrink-0">
          {open
            ? loading
              ? <span className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
              : <Search className="w-4 h-4 text-blue-500" />
            : value
              ? <X className="w-4 h-4 hover:text-gray-600" onClick={handleClear} />
              : <ChevronDown className="w-4 h-4" />
          }
        </div>
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {!query.trim() ? (
            <p className="px-3 py-3 text-sm text-gray-400 text-center">Start typing to search…</p>
          ) : loading ? (
            <p className="px-3 py-3 text-sm text-gray-400 text-center">Searching…</p>
          ) : results.length === 0 ? (
            <p className="px-3 py-3 text-sm text-gray-400 text-center">No clients found for «{query}»</p>
          ) : (
            <>
              <div className="px-3 py-1.5 text-xs text-gray-400 border-b border-gray-100 bg-gray-50">
                {results.length} result{results.length !== 1 ? 's' : ''}
              </div>
              <ul className="max-h-56 overflow-y-auto">
                {results.map(opt => (
                  <li
                    key={opt.id}
                    onMouseDown={() => handleSelect(opt)}
                    className={`px-3 py-2.5 text-sm cursor-pointer flex items-center gap-2 ${
                      opt.id === value
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-medium">{opt.label}</div>
                      <div className="flex gap-2 mt-0.5">
                        {opt.publicId && (
                          <span className="text-xs font-mono text-blue-500">{formatPublicIdDisplay(opt.publicId)}</span>
                        )}
                        {opt.email && opt.email !== opt.label && (
                          <span className="text-xs text-gray-400 truncate">{opt.email}</span>
                        )}
                      </div>
                    </div>
                    {opt.id === value && (
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default AccountsPage;
